import * as React from 'react';

import type {Flow} from 'flow-to-typescript-codemod';
import type {AccountUpdateInput, SessionUser} from 'nutshell-graphql-types';

import {Label as SharedLabel, LoadingState, ErrorState} from '../../peep-details-section';

import {PEEP_ASSIGNEE_SELECT_ID, PeepAssigneeSelect} from '../peep-assignee-select';
import {PEEP_TERRITORY_SELECT_ID, PeepTerritorySelect} from '../peep-territory-select';

import {INDUSTRY_SELECT_ID, AccountIndustrySelect} from './account-industry-select';
import {
    NUMBER_EMPLOYEES_INPUT_FIELD_ID,
    AccountNumberOfEmployeesField,
} from './account-number-of-employees-field';
import {REVENUE_INPUT_FIELD_ID, AccountRevenueField} from './account-revenue-field';
import {ACCOUNT_TYPE_SELECT_ID, AccountTypeSelect} from './account-type-select';
import {useGetAccountInfo} from './use-get-account-info';
import {useUpdateAccountInfo} from './use-update-account-info';

import styles from './account-details-section.module.css';

type Props = {
    accountId: string;
    isModelLoading: boolean;
    currentUser: SessionUser | null | undefined;
};

export function AccountDetailsSection(props: Props) {
    const {accountId, isModelLoading, currentUser} = props;

    const [isUpdating, setIsUpdating] = React.useState<boolean>(false);

    const {accountInfo, isLoading, errorMessage} = useGetAccountInfo(accountId);
    const updateAccountInfo = useUpdateAccountInfo();

    const handleUpdateAccountInfo = (
        updatedInfo: // Flow workaround to include only updatable fields
        Flow.Diff<
            AccountUpdateInput,
            {
                accountId: any;
            }
        >
    ) => {
        if (!isUpdating) {
            setIsUpdating(true);

            updateAccountInfo({accountId, ...updatedInfo}).then(() => {
                setIsUpdating(false);
            });
        }
    };

    if (isModelLoading || isLoading || !currentUser) {
        return <LoadingState />;
    }

    const isDisabled = !accountInfo;

    const Label = (label: {htmlFor: string; children: React.ReactNode}) => (
        <SharedLabel htmlFor={label.htmlFor} isDisabled={isDisabled}>
            {label.children}
        </SharedLabel>
    );

    return (
        <>
            {errorMessage ? <ErrorState /> : undefined}
            <div>
                <Label htmlFor={ACCOUNT_TYPE_SELECT_ID}>Company type</Label>
                <AccountTypeSelect
                    accountTypeId={accountInfo ? accountInfo.accountType.id : undefined}
                    onUpdateAccountType={(accountTypeId: string) => {
                        handleUpdateAccountInfo({accountTypeId});
                    }}
                    isDisabled={isDisabled}
                    canAccessSetup={currentUser.permissions.canAccessSetup}
                />
            </div>
            <div>
                <Label htmlFor={INDUSTRY_SELECT_ID}>Industry</Label>
                <AccountIndustrySelect
                    industryId={
                        accountInfo && accountInfo.industry ? accountInfo.industry.id : undefined
                    }
                    onUpdateIndustry={(industryId?: string | null) => {
                        handleUpdateAccountInfo({industryId});
                    }}
                    isDisabled={isDisabled}
                    canAccessSetup={currentUser.permissions.canAccessSetup}
                />
            </div>
            <div>
                <Label htmlFor={PEEP_ASSIGNEE_SELECT_ID}>Assignee</Label>
                <PeepAssigneeSelect
                    assigneeId={accountInfo && accountInfo.owner ? accountInfo.owner.id : undefined}
                    onUpdatePeepAssignee={(assigneeId?: string | null) => {
                        handleUpdateAccountInfo({assigneeId});
                    }}
                    isDisabled={isDisabled || !currentUser.permissions.canAssignEntities}
                />
            </div>
            {NutClientConfig.hasTerritories ? (
                <div>
                    <Label htmlFor={PEEP_TERRITORY_SELECT_ID}>Territory</Label>
                    <PeepTerritorySelect
                        territoryId={
                            accountInfo && accountInfo.territory
                                ? accountInfo.territory.id
                                : undefined
                        }
                        onUpdatePeepTerritory={(territoryId?: string | null) => {
                            handleUpdateAccountInfo({territoryId});
                        }}
                        isDisabled={isDisabled}
                        canAccessSetup={currentUser.permissions.canAccessSetup}
                    />
                </div>
            ) : undefined}
            <div className={styles['two-column-fields']}>
                <div>
                    <Label htmlFor={REVENUE_INPUT_FIELD_ID}>Annual revenue</Label>
                    <AccountRevenueField
                        revenue={
                            accountInfo && accountInfo.revenue
                                ? accountInfo.revenue.currencyValue.amount
                                : undefined
                        }
                        onUpdateAccountRevenue={(revenue?: number | null) => {
                            handleUpdateAccountInfo({revenue});
                        }}
                        isDisabled={isDisabled}
                    />
                </div>
                <div>
                    <Label htmlFor={NUMBER_EMPLOYEES_INPUT_FIELD_ID}>Number of employees</Label>
                    <AccountNumberOfEmployeesField
                        numberOfEmployees={accountInfo ? accountInfo.employeeCount : undefined}
                        onUpdateNumberOfEmployees={(employeeCount?: number | null) => {
                            handleUpdateAccountInfo({employeeCount});
                        }}
                        isDisabled={isDisabled}
                    />
                </div>
            </div>
        </>
    );
}
